/**
 * SEO landing for the query cluster "online személyi edző".
 *
 * A location-independent counterpart to the Miskolc landing:
 *  - single H1 with the target keyword
 *  - unique long-form Hungarian copy that emphasises flexibility + weekly
 *    video technique review (not room / gym location)
 *  - Service JSON-LD (Schema.org) with `areaServed = Country: HU`
 *    (no PostalAddress — this is an online service)
 *  - FAQPage JSON-LD (6 questions) merged into the same @graph
 *  - cross-link to /szemelyi-edzo-miskolc for visitors who ARE in Miskolc
 *  - CTA routes to /#contact (shared free-consultation booking calendar)
 *
 * Listed in sitemap.xml (routes/seo.py) and reserved from LegacyBlogRedirect.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Globe, ChevronRight, CheckCircle2, ChevronDown, Video, Wifi, Zap, MapPin,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useDocumentMeta } from '../utils/useDocumentMeta';
import { Button } from '../components/ui/button';
import QualificationDialog from '../components/QualificationDialog';
import BookingModal from '../components/BookingModal';
import ReviewsSection from '../components/ReviewsSection';
import PromoPopupModal from '../components/PromoPopupModal';
import usePromoPopup from '../hooks/usePromoPopup';
import PhotoSlider from '../components/PhotoSlider';
import { ABOUT_PHOTOS_SMALL } from '../data/aboutPhotos';
import PromoHeroCard from '../components/PromoHeroCard';
import { trackLead } from '../utils/metaPixel';

const SITE_ORIGIN = 'https://oroszcoaching.hu';
const CANONICAL = `${SITE_ORIGIN}/online-szemelyi-edzo`;

const FAQ_ITEMS = [
  {
    q: 'Mennyibe kerül az online személyi edzés?',
    a: ('Az online coaching havi 25.000 Ft-tól indul. A pontos csomagok tartalmát a Programok oldalon találod. '
        + 'Online formában nincs terembérleti díj. Ha edzőterembe jársz, annak költsége külön értendő.'),
  },
  {
    q: 'Működik az online edzés, ha még sosem edzettem?',
    a: ('Igen. Kezdőként pont a folyamatos technikai visszajelzés a legértékesebb: a videós elemzéssel a rossz '
        + 'beidegződések már az elején kiszűrhetők, biztonságosan.'),
  },
  {
    q: 'Milyen eszköz kell hozzá?',
    a: ('A tervet a rendelkezésedre álló felszereléshez igazítom — legyen szó jól felszerelt edzőteremről, '
        + 'otthoni minimál eszközről vagy a kettő közöttiről. A felmérésnél átbeszéljük, mid van.'),
  },
  {
    q: 'Hogyan zajlik a kapcsolattartás?',
    a: ('Heti rendszerességgel: te felveszed a gyakorlataidat, én elemzem a technikát és a haladást, majd ez '
        + 'alapján finomítjuk a tervet. A részleteket az első konzultáción beszéljük meg.'),
  },
  {
    q: 'Az egész országból elérhető?',
    a: 'Igen — az online forma Magyarország (és külföldön élő magyarok számára a világ) bármely pontjáról működik.',
  },
  {
    q: 'Van ingyenes alkalom?',
    a: 'Az első, 20 perces ismerkedő konzultáció ingyenes és kötelezettségmentes. Személyesen a BB Gymben vagy online – ahogy neked kényelmes.',
  },
];

// Service (no address) + FAQPage JSON-LD merged as an @graph.
const ONLINE_REVIEWS = [
  {
    name: 'Patricia',
    source: 'Google-értékelés',
    content:
      'Kedves, türelmes, értelmesen magyaráz. […] Én az online coachingot választottam, és nem bántam meg. Egyszerű a felület használata, és külön pont, hogy a fejlődésemet is láthatom.',
  },
  {
    name: 'Miklós',
    source: 'Google-értékelés',
    content:
      'Az online coachingnak köszönhetően nagyon egyszerűen és folyamatosan tudjuk tartani a kapcsolatot, így akkor is számíthatok a segítségére, amikor éppen nem személyesen edzünk. […] Az elmúlt körülbelül két és fél hónap alatt 11 kilót sikerült leadnom.',
  },
];

const buildJsonLd = () => ({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type':      'Service',
      '@id':        `${CANONICAL}#service`,
      serviceType:  'Online személyi edzés',
      name:         'Online személyi edző – Orosz Coaching',
      description:  ('Tudományosan megalapozott, személyre szabott online személyi edzés az ország bármely '
                    + 'pontjáról, heti videós technikaelemzéssel.'),
      url:          CANONICAL,
      image:        `${SITE_ORIGIN}/og-image.png`,
      provider: {
        '@type':   'Person',
        name:      'Orosz Gábor',
        jobTitle:  'Okleveles testépítés-fitnesz sportedző',
        url:       SITE_ORIGIN,
      },
      areaServed: { '@type': 'Country', name: 'Magyarország' },
      availableChannel: {
        '@type':    'ServiceChannel',
        serviceUrl: CANONICAL,
      },
      offers: {
        '@type':          'Offer',
        price:            '25000',
        priceCurrency:    'HUF',
        description:      'Online coaching havidíj, 25.000 Ft-tól',
      },
    },
    {
      '@type': 'FAQPage',
      mainEntity: FAQ_ITEMS.map((item) => ({
        '@type':          'Question',
        name:             item.q,
        acceptedAnswer:   { '@type': 'Answer', text: item.a },
      })),
    },
  ],
});

const AUDIENCE_ROWS = [
  { label: 'Akinek nincs a közelében megfelelő edző',
    body:  'de nem akar terv és kísérés nélkül edzeni.' },
  { label: 'Elfoglaltaknak és műszakosoknak',
    body:  'akiknek a rugalmasság kulcskérdés — te döntöd el, mikor edzel.' },
  { label: 'Kezdőknek',
    body:  'akik biztos technikai alapokat és folyamatos visszajelzést szeretnének, mielőtt egyedül belevágnának.' },
  { label: 'Haladóknak',
    body:  'akik mérhető izomtömeg- és erőnövekedést szeretnének tudományos alapú tervezéssel.' },
];

const HIGHLIGHT_ROWS = [
  {
    Icon: Video, title: 'Heti videós technikaelemzés',
    body: 'Te felveszed, én elemzem, így nem rögzül be rossz mozdulat.',
  },
  {
    Icon: Wifi, title: 'Bárhonnan működik',
    body: 'Magyarország (és külföld) bármely pontjáról — a saját edzőhelyeden, a saját idődben.',
  },
  {
    Icon: Zap, title: 'Tudományos alapú tervezés',
    body: 'Semmi divatdiéta és sablonprogram — csak az, ami bizonyítottan működik.',
  },
];

const FaqItem = ({ q, a, idx }) => {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="border-b border-gray-200 last:border-b-0" data-testid={`faq-item-${idx}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left py-5 flex items-center justify-between gap-4 group"
        aria-expanded={open}
      >
        <span className="text-base md:text-lg font-semibold text-gray-900 group-hover:text-yellow-700 transition-colors">
          {q}
        </span>
        <ChevronDown
          size={20}
          className={`text-gray-400 shrink-0 transition-transform ${open ? 'rotate-180 text-yellow-600' : ''}`}
        />
      </button>
      {/* A válasz mindig a DOM-ban van (SEO), csak vizuálisan csukott. */}
      <div
        className={`grid transition-all duration-300 ${open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
        aria-hidden={!open}
      >
        <p className="overflow-hidden text-gray-700 leading-relaxed pr-8" data-testid={`faq-answer-${idx}`}>
          <span className="block pb-5">{a}</span>
        </p>
      </div>
    </div>
  );
};

export default function OnlineSzemelyiEdzoPage() {

  useDocumentMeta({
    title:       'Online személyi edző – tudományos alapú edzésterv otthonra | Orosz Coaching',
    description: ('Online személyi edző: tudományosan megalapozott, rád szabott edzésterv az ország bármely '
                 + 'pontjáról. Heti videós technikaelemzés és folyamatos kísérés. Ingyenes első konzultáció.'),
    canonical:   CANONICAL,
    image:       `${SITE_ORIGIN}/og-image.png`,
    type:        'website',
    jsonLd:      buildJsonLd(),
    keywords:    ('online személyi edző, online edzésterv, online coaching, online személyi edzés, '
                 + 'edzésterv otthonra, tudományos alapú edzésterv, online edző kezdőknek, izomépítés online, '
                 + 'fogyás online edzővel, Orosz Gábor'),
  });

  const [bookingOpen, setBookingOpen] = useState(false);
  const goToBooking = () => setBookingOpen(true);

  // Promo popup — shares the same schedule/cooldown/version as the homepage.
  const promoPopup = usePromoPopup({ enabled: false });
  const handlePromoCta = () => {
    trackLead({ content_name: `promo_popup:${promoPopup.promo?.version || 'default'}:online` });
    promoPopup.reportClick();
    promoPopup.close();
    if (promoPopup.promo?.cta_action === 'link' && promoPopup.promo?.cta_href) {
      window.location.assign(promoPopup.promo.cta_href);
      return;
    }
    try { sessionStorage.setItem('oc_from_promo_cta', promoPopup.promo?.version || '1'); } catch { /* ignore */ }
    setBookingOpen(true);
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <Header onBookClick={goToBooking} />

      {/* ============================================================ */}
      {/*  HERO — single H1 with the target keyword                    */}
      {/* ============================================================ */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 text-white overflow-hidden pt-28 md:pt-32 pb-16 md:pb-20">
        <div className="absolute inset-0 opacity-10 pointer-events-none"
             style={{ backgroundImage: "radial-gradient(circle at 15% 25%, rgba(56,189,248,0.35), transparent 45%), radial-gradient(circle at 85% 65%, rgba(202,138,4,0.25), transparent 45%)" }} />
        <div className="relative container mx-auto px-6 max-w-6xl grid lg:grid-cols-[minmax(0,1fr)_370px] gap-10 lg:gap-14 items-center">
          <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-sky-400/40 bg-sky-500/10 text-sky-300 text-[11px] font-bold uppercase tracking-widest px-3 py-1.5 mb-5">
            <Globe size={12} /> Online · Magyarország egésze
          </div>
          <h1
            data-testid="seo-page-h1"
            className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-5"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Online személyi edző —<br className="hidden md:block" /> tudományos alapú edzésterv, bárhonnan
          </h1>
          <p className="text-lg md:text-xl text-white/85 leading-relaxed max-w-3xl mb-8">
            Rád szabott online személyi edzés az ország bármely pontjáról: nem sablonprogram, hanem a te <span className="text-sky-300 font-semibold">testedhez, időbeosztásodhoz és céljaidhoz</span> igazított terv, heti videós technikaelemzéssel.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button
              data-testid="seo-hero-cta-book"
              onClick={goToBooking}
              className="bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold text-base h-12 px-6"
            >
              Foglalj ingyenes konzultációt <ChevronRight size={18} className="ml-1" />
            </Button>
            <a
              href="#igy-dolgozunk"
              data-testid="seo-hero-cta-flow"
              className="inline-flex items-center rounded-md border border-white/25 hover:border-sky-400/60 hover:text-sky-300 text-white font-semibold h-12 px-6 transition-colors"
            >
              Hogyan dolgozom
            </a>
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/70">
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={14} className="text-emerald-400" /> Ingyenes első konzultáció</span>
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={14} className="text-emerald-400" /> Heti videós technikaelemzés</span>
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={14} className="text-emerald-400" /> Rugalmas időbeosztás</span>
          </div>
          </div>
          <div className="lg:justify-self-end w-full flex lg:block justify-center">
            <PromoHeroCard onBooking={goToBooking} testId="promo-hero-card-online" />
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  BODY                                                        */}
      {/* ============================================================ */}
      <section className="container mx-auto px-6 py-16 md:py-20 max-w-3xl">
        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,260px)_1fr] gap-6 md:gap-8 items-start mb-10">
          <div className="rounded-lg overflow-hidden shadow-xl aspect-[4/5] md:aspect-auto md:h-[320px] w-full max-w-sm mx-auto md:max-w-none md:mx-0">
            <PhotoSlider
              photos={ABOUT_PHOTOS_SMALL}
              testId="online-about-photo"
              className="w-full h-full"
            />
          </div>
          <p className="text-lg md:text-xl text-gray-700 leading-relaxed">
            <strong>Orosz Gábor</strong> vagyok, okleveles{' '}
            <QualificationDialog
              label="testépítés-fitnesz sportedző"
              testId="online-qualification-trigger"
            />
            . Ha online személyi edzőt keresel, aki nem egy letölthető PDF-et ad a kezedbe, hanem valódi, folyamatos kísérést, akkor jó helyen jársz. Nem számít, hol élsz Magyarországon — a tudományosan megalapozott megközelítés, a heti visszajelzés és a személyre szabott tervezés ugyanúgy működik távolról, mint személyesen.
          </p>
        </div>

        {/* Highlight grid — 3 pillars */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-12">
          {HIGHLIGHT_ROWS.map(({ Icon, title, body }) => (
            <div key={title} className="rounded-xl border border-gray-200 bg-white p-5">
              <Icon size={22} className="text-sky-500 mb-2" />
              <div className="font-bold text-gray-900 mb-1">{title}</div>
              <p className="text-sm text-gray-600 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>

        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          Miért működik az online coaching?
        </h2>
        <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-10">
          Sokan azt hiszik, az online edzés annyi, hogy kapsz egy tervet, aztán magadra maradsz. Nálam nem így van. A közös munka lényege ugyanaz, mint személyesen: folyamatosan figyelem a haladásodat, elemzem a technikádat videón, és a tervet hétről hétre a te valóságodhoz igazítom. A különbség csak annyi, hogy nem kell egy helyre eljutnod — a saját edzőtermedben, otthon vagy bárhol edzhetsz, a saját időbeosztásodban.
        </p>

        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          Kinek való az online forma?
        </h2>
        <ul className="space-y-3 mb-10">
          {AUDIENCE_ROWS.map((row) => (
            <li key={row.label} className="flex gap-3 items-start">
              <CheckCircle2 size={20} className="text-sky-500 shrink-0 mt-0.5" />
              <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                <strong className="text-gray-900">{row.label}</strong>, {row.body}
              </p>
            </li>
          ))}
        </ul>

        <h2
          id="igy-dolgozunk"
          className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 scroll-mt-24"
          style={{ fontFamily: 'Montserrat, sans-serif' }}
        >
          Hogyan néz ki a közös munka?
        </h2>
        <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-10">
          Az együttműködés részletes felméréssel és anamnézissel indul — a céljaid, az eddigi tapasztalataid, a rendelkezésre álló eszközeid és az időbeosztásod alapján. Ezután kapsz egy edzéstervet, amit a te helyzetedre építek, és amelyet heti rendszerességgel közösen finomítunk a videós forma- és technikaelemzés alapján. Nem maradsz magadra: a folyamatos kapcsolat és a heti visszajelzés az, ami az online formát is működővé teszi.
        </p>

        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          Miben más a megközelítésem?
        </h2>
        <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-12">
          Nem hiszek a divatdiétákban és a sablonprogramokban. Minden döntés mögött a sporttudomány jelenlegi állása áll. A cél nem a leggyorsabb, hanem a leghosszabb távon is fenntartható eredmény — egy olyan tudás, amit később önállóan is tudsz alkalmazni.
        </p>
      </section>

      <ReviewsSection title="Amit az online ügyfeleim mondanak" reviews={ONLINE_REVIEWS} />

      <section className="container mx-auto px-6 pb-16 md:pb-20 max-w-3xl">
        {/* Cross-link to Miskolc — helps both users and Google's topical graph */}
        <div
          data-testid="online-to-miskolc-cta"
          className="rounded-2xl border border-yellow-200 bg-yellow-50/60 p-5 md:p-6 mb-12 flex flex-col md:flex-row items-start md:items-center gap-4"
        >
          <MapPin size={20} className="text-yellow-700 shrink-0 mt-1 md:mt-0" />
          <div className="flex-1">
            <div className="font-bold text-gray-900">Miskolci vagy?</div>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed mt-0.5">
              Személyes 1:1 és max. 3 fős kiscsoportos edzésekkel is várlak a BB Gymben.
            </p>
          </div>
          <Link
            to="/szemelyi-edzo-miskolc"
            data-testid="link-to-miskolc"
            className="inline-flex items-center rounded-md bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold h-11 px-5 whitespace-nowrap transition-colors"
          >
            Miskolci oldal <ChevronRight size={16} className="ml-1" />
          </Link>
        </div>

        {/* CTA card */}
        <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 text-white p-8 md:p-10">
          <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Az első lépés ingyenes
          </h2>
          <p className="text-white/85 leading-relaxed mb-6 max-w-2xl">
            Foglalj egy kötetlen, 20 perces ingyenes konzultációt, ahol átbeszéljük, honnan indulsz, mit szeretnél elérni, és hogy az online forma passzol-e hozzád. Semmi elköteleződés. Személyesen a BB Gymben vagy online – ahogy neked kényelmes.
          </p>
          <Button
            data-testid="seo-bottom-cta-book"
            onClick={goToBooking}
            className="bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold text-base h-12 px-6"
          >
            Foglalj ingyenes konzultációt <ChevronRight size={18} className="ml-1" />
          </Button>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  FAQ                                                         */}
      {/* ============================================================ */}
      <section className="bg-gray-50 border-t border-gray-200 py-16 md:py-20">
        <div className="container mx-auto px-6 max-w-3xl">
          <h2
            className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 text-center"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Gyakori kérdések
          </h2>
          <p className="text-gray-600 text-center mb-8">
            Ha bármit nem találsz itt, írj bátran a <a href="mailto:info@oroszcoaching.hu" className="text-yellow-700 hover:text-yellow-800 font-semibold underline underline-offset-2">info@oroszcoaching.hu</a> címre.
          </p>
          <div className="rounded-2xl border border-gray-200 bg-white px-6" data-testid="seo-faq-list">
            {FAQ_ITEMS.map((item, i) => (
              <FaqItem key={item.q} q={item.q} a={item.a} idx={i} />
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <BookingModal isOpen={bookingOpen} onClose={() => setBookingOpen(false)} />
      <PromoPopupModal
        open={promoPopup.isOpen}
        promo={promoPopup.promo}
        onCtaClick={handlePromoCta}
        onClose={promoPopup.close}
      />
    </div>
  );
}
